import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, Input, InputProps } from '@progress/kendo-react-inputs';
import { isEmpty, trimEnd, trimStart } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
// import Checkbox from 'src/components/design-system/Checkbox';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useCopyDepartmentMutation from 'src/services/department/useCopyDepartmentMutation';
import { Department } from 'src/types/department';
import { getBgColor, getIcon } from 'src/utils/icons';

interface CopyDepartmentProps {
  selectedRows: Department[];
}

const CopyDepartment: FC<CopyDepartmentProps> = ({ selectedRows }) => {
  const { t, companyId, departments, setSelectedRows } = useViewDepartment();

  const departmentId = selectedRows[0]?.departmentId;

  const onSuccess = async () => {
    handleCloseModal();
    toast.success('Department successfully created');
    setSelectedRows([]);
    setDepartmentCopyName('');
  };

  const { mutateAsync: addCopyDepartment, isPending: isLoading } = useCopyDepartmentMutation({
    companyId: Number(companyId),
    sourceDepartmentId: departmentId,
    onSuccess,
  });

  const { value: isModalOpen, setFalse: handleCloseModal, setTrue: openModal } = useBoolean();
  const [departmentCopyName, setDepartmentCopyName] = useState('');
  const [isNonDeletedGLCodesCopied, setIsNonDeletedGLCodesCopied] = useState(true);
  const [isDeletedGLCodesCopied, setIsDeletedGLCodesCopied] = useState(false);
  const [isNonDisabledDeptMembersCopied, setIsNonDisabledDeptMembersCopied] = useState(true);
  const [isDisabledDeptMembersCopied, setIsDisabledDeptMembersCopied] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const deletedRecords = selectedRows.map((d) => ({
    ...d,
    members: d.members.filter((member) => member.isDeleted),
    glCodes: d.glCodes.filter((glCode) => glCode.isDeleted),
  }));
  const NonDeletedRecords = selectedRows.map((d) => ({
    ...d,
    members: d.members.filter((member) => !member.isDeleted),
    glCodes: d.glCodes.filter((glCode) => !glCode.isDeleted),
  }));
  const { isCopyDisable, isConfirmCopyDisable, isDuplicate, message, confirmMessage } =
    useMemo(() => {
      const isCopyDisable = selectedRows.length !== 1;
      const isConfirmCopyDisable = !departmentCopyName.length;

      const isDuplicate = departments
        ?.map((el) => el.name?.toLowerCase())
        .includes(String(trimEnd(departmentCopyName))?.toLowerCase());
      let message = '';
      let confirmMessage = '';

      if (isDuplicate) {
        confirmMessage = 'Department exists. Please enter unique Department Name';
      }
      if (isConfirmCopyDisable) {
        confirmMessage = 'Please enter a Department Name';
      }
      if (selectedRows.length === 0) {
        message = 'Please select a Department to Copy';
      } else if (selectedRows.length > 1) {
        message = 'Please select only one Department to Copy';
      }
      return {
        isCopyDisable,
        isConfirmCopyDisable,
        message,
        confirmMessage,
        isDuplicate,
      };
    }, [departmentCopyName, departments, selectedRows.length]);

  const handleChange = useCallback((e: InputProps) => {
    if (e.value !== undefined) {
      setDepartmentCopyName(trimStart(e.value as string) as string);
    }
  }, []);

  const handleSubmitCopy = useCallback(async () => {
    addCopyDepartment({
      name: trimEnd(departmentCopyName),
      copyGLCodes: isNonDeletedGLCodesCopied,
      copyMembers: isNonDisabledDeptMembersCopied,
      copyDeletedGLCodes: isDeletedGLCodesCopied,
      copyDeletedMembers: isDisabledDeptMembersCopied,
    });
  }, [
    addCopyDepartment,
    departmentCopyName,
    isDeletedGLCodesCopied,
    isDisabledDeptMembersCopied,
    isNonDeletedGLCodesCopied,
    isNonDisabledDeptMembersCopied,
  ]);

  const handleCopyDepartments = useCallback(() => {
    setDepartmentCopyName('');
    setIsNonDeletedGLCodesCopied(true);
    setIsDeletedGLCodesCopied(false);
    setIsNonDisabledDeptMembersCopied(true);
    setIsDisabledDeptMembersCopied(false);
    openModal();
  }, [openModal]);

  const handleNonDeletedGLCodesCopied = useCallback(() => {
    if (isNonDeletedGLCodesCopied) {
      setIsDeletedGLCodesCopied(false);
    }
    setIsNonDeletedGLCodesCopied((prev) => !prev);
  }, [isNonDeletedGLCodesCopied]);

  const handleNonDisabledDeptMembersCopied = useCallback(() => {
    if (isNonDisabledDeptMembersCopied) {
      setIsDisabledDeptMembersCopied(false);
    }
    setIsNonDisabledDeptMembersCopied((prev) => !prev);
  }, [isNonDisabledDeptMembersCopied]);

  useEffect(() => {
    if (isModalOpen && inputRef?.current) {
      inputRef?.current.focus();
    }
  }, [isModalOpen]);

  const selectedDepartmentName = selectedRows[0]?.name || '';
  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type={'Information'} contents={[message]} />
          </div>
        }
        show={isCopyDisable}
      >
        <Button color="submit" onClick={handleCopyDepartments} disabled={isCopyDisable}>
          {t('Copy')}
        </Button>
      </PopUp>
      <div>
        <Dialog
          width={400}
          id="modal"
          title={
            <>
              <span>{`${t('Copy Department')} - `}</span>
              <span
                data-tooltip-id="global-tooltip"
                data-tooltip-content={selectedDepartmentName}
                data-tooltip-place="bottom"
                style={{ marginLeft: 4 }}
              >
                {selectedDepartmentName?.length > 15
                  ? ` ${selectedDepartmentName?.slice(0, 17)}...`
                  : ` ${selectedDepartmentName}`}
              </span>
            </>
          }
          open={isModalOpen}
          onClose={handleCloseModal}
          closeIcon={!isLoading}
          data-tooltip-place="bottom"
          data-tooltip-id={'global-tooltip'}
          data-tooltip-content={selectedRows[0]?.name}
        >
          <>
            <PopUp
              popupAlign={{ horizontal: 'right', vertical: 'top' }}
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type={'Error'} contents={[confirmMessage]} />
                </div>
              }
              show={isConfirmCopyDisable || isDuplicate}
            >
              <div
                className={`pt-2 p-1 ${getBgColor(
                  confirmMessage ? [confirmMessage] : []
                )} text-[13px]`}
              >
                <div className="font-bold flex items-center pb-2">
                  New Department Name
                  <span className="px-2">{getIcon(confirmMessage ? [confirmMessage] : [])}</span>
                </div>
                <Input
                  value={trimStart(departmentCopyName)}
                  onChange={handleChange}
                  valid={!isDuplicate && !isEmpty(departmentCopyName)}
                  ref={inputRef}
                />
              </div>
            </PopUp>
            <div className="flex items-center pt-3">Include:</div>
            <ul
              className="p-4 m-1 overflow-auto flex flex-col pt-1 ml-0 pl-0"
              style={{ height: 'auto' }}
            >
              <Checkbox
                checked={isNonDeletedGLCodesCopied}
                onChange={handleNonDeletedGLCodesCopied}
                className="mt-1 px-1"
                label={`GL Codes (${NonDeletedRecords[0]?.glCodes.length})`}
              />
              {isNonDeletedGLCodesCopied && (
                <div className="ml-4">
                  <Checkbox
                    checked={isDeletedGLCodesCopied}
                    onChange={() => setIsDeletedGLCodesCopied((prev) => !prev)}
                    className="mt-1"
                    label={`Deleted GL Codes (${deletedRecords[0]?.glCodes.length})`}
                  />
                </div>
              )}

              <Checkbox
                checked={isNonDisabledDeptMembersCopied}
                onChange={handleNonDisabledDeptMembersCopied}
                className="mt-1 px-1"
                label={`Department Members (${NonDeletedRecords[0]?.members.length})`}
              />
              {isNonDisabledDeptMembersCopied && (
                <div className="ml-4">
                  <Checkbox
                    checked={isDisabledDeptMembersCopied}
                    onChange={() => setIsDisabledDeptMembersCopied((prev) => !prev)}
                    className="mt-1"
                    label={`Disabled Members
                    (${deletedRecords[0]?.members.length})`}
                  />
                </div>
              )}
            </ul>
          </>
          <DialogActionsBar>
            <div className="flex flex-row items-center justify-end gap-2">
              <PopUp
                popupAlign={{ horizontal: 'center', vertical: 'top' }}
                content={
                  <div className="m-1 max-w-[350px]">
                    <PopUpContentWrapper type={'Error'} contents={[confirmMessage]} />
                  </div>
                }
                show={isConfirmCopyDisable || isDuplicate}
              >
                <Button
                  color="info"
                  onClick={handleSubmitCopy}
                  disabled={isConfirmCopyDisable || isDuplicate || isLoading}
                  loading={isLoading}
                >
                  {!isNonDeletedGLCodesCopied && !isNonDisabledDeptMembersCopied
                    ? t('Create')
                    : t('Copy')}
                </Button>
              </PopUp>
              <Button color="info" onClick={handleCloseModal} disabled={isLoading}>
                {t('Cancel')}
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      </div>
    </>
  );
};

export default CopyDepartment;
